import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Heart, Star as StarIcon, Sparkles, Rainbow as RainbowIcon, Smile, X, Volume2, VolumeX } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Sparkle } from "@/components/Decor";
import { Input } from "@/components/ui/input";
import ReaderPage from "@/components/ReaderPage";
import { mediaUrl } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUB = `${BACKEND_URL}/api/public`;

const STICKERS = [
    { type: "heart", icon: Heart, color: "#FF6B9D", fill: true },
    { type: "star", icon: StarIcon, color: "#F59E0B", fill: true },
    { type: "sparkle", icon: Sparkles, color: "#C4B5FD", fill: false },
    { type: "rainbow", icon: RainbowIcon, color: "#7DD3FC", fill: false },
    { type: "smile", icon: Smile, color: "#86EFAC", fill: false },
];

export default function SharedReader() {
    const { token } = useParams();
    const [book, setBook] = useState(null);
    const [idx, setIdx] = useState(0);
    // Clamped on the way out of storage too — this came from the browser, not us.
    const [nickname, setNickname] = useState(() => (localStorage.getItem("mt_nick") || "").slice(0, 32));
    const [askName, setAskName] = useState(() => !localStorage.getItem("mt_nick"));
    const [selectedSticker, setSelectedSticker] = useState(null);
    const [narrating, setNarrating] = useState(false);
    const leftBoxRef = useRef(null);
    const rightBoxRef = useRef(null);
    const audioRef = useRef(null);
    const [turning, setTurning] = useState(null); // null | "fwd" | "back"

    useEffect(() => {
        axios.get(`${PUB}/books/${token}`)
            .then(({ data }) => setBook(data))
            .catch((e) => toast.error(e?.response?.data?.detail || "This link has been closed."));
    }, [token]);

    useEffect(() => {
        const noContext = (e) => e.preventDefault();
        document.addEventListener("contextmenu", noContext);
        return () => {
            document.removeEventListener("contextmenu", noContext);
            // Leaving the page must stop the family recording too, not just the TTS.
            try { window.speechSynthesis.cancel(); } catch {}
            if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        };
    }, []);

    if (!book) return <div className="min-h-screen reader-night grid place-items-center font-display text-2xl text-paper">Loading…</div>;
    const pages = book.pages || [];
    // `idx` is the focused page so the narrator can walk one page at a time;
    // the open spread is the leaf-pair that page falls in.
    const spreadStart = idx - (idx % 2);
    const leftPage = pages[spreadStart];
    const rightPage = pages[spreadStart + 1];
    const reactionsFor = (p) => (book.reactions && book.reactions[String(p?.page_number)]) || [];
    const stickerConfig = (type) => STICKERS.find((x) => x.type === type) || STICKERS[0];

    const stopNarrator = () => {
        try { window.speechSynthesis.cancel(); } catch {}
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        setNarrating(false);
    };

    const advanceFrom = (cur) => {
        const nx = cur + 1;
        if (nx >= pages.length) { setNarrating(false); return cur; }
        setTimeout(() => speakPage(nx), 400);
        return nx;
    };

    const speakPage = (pageIdx) => {
        const p = pages[pageIdx];
        if (!p) return;

        // The whole point of sending Nani the link is that she hears the family,
        // not a browser voice. Share payloads carry `voices`; play them here too.
        const recorded = (book.voices || {})[String(p.page_number)];
        if (recorded?.url) {
            try { window.speechSynthesis.cancel(); } catch {}
            if (audioRef.current) audioRef.current.pause();
            const audio = new Audio(mediaUrl(recorded.url));
            audio.onended = () => setIdx(advanceFrom);
            audio.onerror = () => setNarrating(false);
            audioRef.current = audio;
            audio.play().catch(() => setNarrating(false));
            return;
        }

        if (!("speechSynthesis" in window)) return;
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(`Page ${p.page_number}. ${p.text}`);
        const voices = window.speechSynthesis.getVoices();
        const preferred = voices.find((v) => /female|samantha|karen|serena|zira|joanna|aria/i.test(v.name)) || voices[0];
        if (preferred) utter.voice = preferred;
        utter.rate = 0.92; utter.pitch = 1.05;
        utter.onend = () => setIdx(advanceFrom);
        utter.onerror = () => setNarrating(false);
        window.speechSynthesis.speak(utter);
    };
    const toggleNarrator = () => { if (narrating) { stopNarrator(); return; } setNarrating(true); speakPage(idx); };

    const TURN_MS = 620;
    const next = () => {
        if (turning || spreadStart + 2 >= pages.length) return;
        stopNarrator(); setTurning("fwd");
        setTimeout(() => { setIdx(spreadStart + 2); setTurning(null); }, TURN_MS);
    };
    const prev = () => {
        if (turning || spreadStart === 0) return;
        stopNarrator(); setTurning("back");
        setTimeout(() => { setIdx(spreadStart - 2); setTurning(null); }, TURN_MS);
    };

    const saveNick = () => {
        const n = nickname.trim().slice(0, 32) || "Someone";
        setNickname(n);
        localStorage.setItem("mt_nick", n);
        setAskName(false);
    };

    // x/y are fractions of the clicked leaf, matching how every sticker placed
    // before the spread landed was stored.
    const placeSticker = (target, boxRef) => async (e) => {
        if (!selectedSticker || !target) return;
        if (askName || !nickname) { setAskName(true); return; }
        const rect = boxRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        try {
            const { data } = await axios.post(`${PUB}/books/${token}/reactions`, {
                page_number: target.page_number, type: selectedSticker, x, y, nickname,
            });
            const key = String(target.page_number);
            const cur = book.reactions?.[key] || [];
            setBook({ ...book, reactions: { ...(book.reactions || {}), [key]: [...cur, data.reaction] } });
            toast.success(`Sticker added by ${nickname} ✨`);
        } catch { toast.error("Couldn't add sticker"); }
    };

    return (
        <div className="min-h-screen reader-night">
            <Toaster position="top-center" richColors />
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-plum/50 border-b border-paper/10">
                <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <span className="w-9 h-9 rounded-2xl grid place-items-center shadow-md"
                            style={{ background: "linear-gradient(135deg,#FF6B9D,#FDE68A,#7DD3FC)" }}>
                            <Sparkle size={16} color="#fff" />
                        </span>
                        <span className="font-display text-xl text-paper">Memory<span className="text-bubblegum">Tales</span></span>
                    </Link>
                    <div className="text-xs text-paper/70">
                        {nickname ? <>You're leaving stickers as <b className="text-paper">{nickname}</b> · <button onClick={() => setAskName(true)} className="underline">change</button></> : "Shared storybook"}
                    </div>
                </div>
            </header>

            <div className="max-w-4xl mx-auto px-6 py-6">
                <div className="text-center mb-4">
                    <div className="text-xs tracking-[0.28em] uppercase text-paper/60">A book shared with love</div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight text-paper mt-1" data-testid="shared-title">{book.title}</h1>
                </div>

                {/* Sticker palette */}
                <div className="mx-auto mb-4 flex items-center justify-between card-cloud px-4 py-2.5">
                    <div className="text-xs tracking-[0.2em] uppercase text-plum/60">Leave a sticker</div>
                    <div className="flex items-center gap-2">
                        <button variant="outline" onClick={toggleNarrator} className="w-9 h-9 rounded-full grid place-items-center bg-white border-2 border-plum/10 hover:border-plum/30" data-testid="shared-narrator-btn" title="Read aloud">
                            {narrating ? <VolumeX className="w-4 h-4 text-plum" /> : <Volume2 className="w-4 h-4 text-plum" />}
                        </button>
                        {STICKERS.map((s) => (
                            <button key={s.type}
                                onClick={() => setSelectedSticker(selectedSticker === s.type ? null : s.type)}
                                className={`w-9 h-9 rounded-full grid place-items-center border-2 transition-all ${selectedSticker === s.type ? "border-plum scale-110 bg-white" : "border-transparent hover:bg-white"}`}
                                style={{ boxShadow: selectedSticker === s.type ? `0 0 0 3px ${s.color}55` : "none" }}
                                data-testid={`shared-sticker-${s.type}`}>
                                <s.icon className="w-4 h-4" style={{ color: s.color, fill: s.fill ? s.color : "none" }} />
                            </button>
                        ))}
                    </div>
                </div>

                <div className="relative mx-auto booklet-noselect" style={{ perspective: 2200 }}
                     onContextMenu={(e) => e.preventDefault()}>
                    <div className="relative flex aspect-[3/2] rounded-2xl overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,0.4)]">
                        <ReaderPage
                            side="left"
                            page={leftPage}
                            pageIndex={spreadStart}
                            total={pages.length}
                            boxRef={leftBoxRef}
                            stickers={reactionsFor(leftPage)}
                            stickerConfig={stickerConfig}
                            watermarked
                            showNicknames
                            placing={!!selectedSticker}
                            onPlace={placeSticker(leftPage, leftBoxRef)}
                            style={{
                                transformOrigin: "right center",
                                transform: turning === "back" ? "rotateY(168deg)" : "rotateY(0deg)",
                                transition: `transform ${TURN_MS}ms cubic-bezier(.3,.8,.25,1)`,
                                backfaceVisibility: "hidden",
                            }}
                        />
                        <ReaderPage
                            side="right"
                            page={rightPage}
                            pageIndex={spreadStart + 1}
                            total={pages.length}
                            boxRef={rightBoxRef}
                            stickers={reactionsFor(rightPage)}
                            stickerConfig={stickerConfig}
                            watermarked
                            showNicknames
                            placing={!!selectedSticker}
                            onPlace={placeSticker(rightPage, rightBoxRef)}
                            style={{
                                transformOrigin: "left center",
                                transform: turning === "fwd" ? "rotateY(-168deg)" : "rotateY(0deg)",
                                transition: `transform ${TURN_MS}ms cubic-bezier(.3,.8,.25,1)`,
                                backfaceVisibility: "hidden",
                            }}
                        />
                        <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-1 bg-plum/25" />
                    </div>

                    <div className="flex items-center justify-between mt-6">
                        <button onClick={prev} disabled={spreadStart === 0 || !!turning}
                                className="btn-sticker sky text-sm disabled:opacity-40" data-testid="shared-prev-btn">
                            <ArrowLeft className="w-4 h-4 inline mr-1" /> Previous
                        </button>
                        <div className="text-xs text-paper/60" data-testid="shared-page-count">
                            {rightPage
                                ? `Pages ${leftPage?.page_number}–${rightPage.page_number} of ${pages.length}`
                                : `Page ${leftPage?.page_number} of ${pages.length}`}
                        </div>
                        <button onClick={next} disabled={spreadStart + 2 >= pages.length || !!turning}
                                className="btn-sticker sky text-sm disabled:opacity-40" data-testid="shared-next-btn">
                            Next <ArrowRight className="w-4 h-4 inline ml-1" />
                        </button>
                    </div>
                </div>

                <div className="text-center mt-10 text-sm text-paper/70">
                    Want to make one for a little one you love?{" "}
                    <Link to="/" className="text-bubblegum font-semibold">Try Tales with Terry →</Link>
                </div>
            </div>

            {/* Nickname prompt */}
            {askName && (
                <div className="fixed inset-0 z-50 bg-plum/40 backdrop-blur-sm grid place-items-center p-6">
                    <div className="card-cloud max-w-sm w-full p-7 relative">
                        {nickname && (
                            <button onClick={() => setAskName(false)} className="absolute top-4 right-4 text-plum/50 hover:text-plum" data-testid="nick-close">
                                <X className="w-4 h-4" />
                            </button>
                        )}
                        <div className="text-xs tracking-[0.28em] uppercase text-plum/60 mb-2">Who's leaving these stickers?</div>
                        <h3 className="font-display text-2xl mb-6 text-plum">A little nickname 💌</h3>
                        <Input value={nickname} onChange={(e) => setNickname(e.target.value.slice(0, 32))}
                            placeholder="Nani, Chachu, Ammu…" maxLength={32} className="mb-4 rounded-full h-11 px-4" data-testid="nick-input" />
                        <button onClick={saveNick} disabled={!nickname.trim()}
                            className="w-full btn-sticker disabled:opacity-60" data-testid="nick-save-btn">
                            ✨ Save & start decorating
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
